import { Group } from "@fairfleet/geotab";
import { normalizeGroupName } from "./normalizeGroupName";
import { COMPANY_GROUP_ID } from "@/constants/geotabGroupIds";
import { isGeotabSystemGroup } from "@/utilities/groupIndex";

export type GroupColumn = {
  parent: Group;
  builtIn: Group[];
  groups: Group[];
};

type NameCompare = (a: string, b: string) => number;

export function findCompanyGroup(allGroups: Group[]): Group | null {
  return allGroups.find(group => group.id === COMPANY_GROUP_ID) ?? allGroups[0] ?? null;
}

/** The selected path, or just the company group when nothing is selected yet. */
export function pathOrRoot(selectedPathIds: string[], companyGroupId: string | undefined): string[] {
  if (selectedPathIds.length > 0) return selectedPathIds;
  return companyGroupId ? [companyGroupId] : [];
}

export function normalizePathIds(
  selectedPathIds: string[],
  companyGroupId: string | undefined,
  groupById: Map<string, Group>,
): string[] {
  if (!companyGroupId) return [];
  return pathOrRoot(selectedPathIds, companyGroupId).filter(
    (id, index, arr) => index === arr.indexOf(id) && groupById.has(id),
  );
}

export function buildChildColumns(
  pathGroups: Group[],
  groupById: Map<string, Group>,
  builtInRank: Map<string, number>,
  compare: NameCompare,
): GroupColumn[] {
  const byName = (a: Group, b: Group) => compare(a.name ?? "", b.name ?? "");
  return pathGroups.map(parent => {
    const children = (parent.children ?? [])
      .map(child => groupById.get(child.id))
      .filter((group): group is Group => !!group)
      .sort(byName);

    const builtIn = children
      .filter(child => isGeotabSystemGroup(child.id))
      .sort((a, b) => {
        const rankA = builtInRank.get(normalizeGroupName(a.name)) ?? Number.MAX_SAFE_INTEGER;
        const rankB = builtInRank.get(normalizeGroupName(b.name)) ?? Number.MAX_SAFE_INTEGER;
        if (rankA !== rankB) return rankA - rankB;
        return byName(a, b);
      });

    const groups = children.filter(child => !isGeotabSystemGroup(child.id)).sort(byName);

    return { parent, builtIn, groups };
  });
}

export function resolveParentId(
  group: Group | null,
  parentByChildId: Map<string, string>,
): string | null {
  if (!group) return null;
  return group.parent?.id ?? parentByChildId.get(group.id) ?? null;
}

export function canModifyGroup(group: Group | null, companyGroupId: string | undefined): boolean {
  return !!group && !isGeotabSystemGroup(group.id) && group.id !== companyGroupId;
}
